import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, User, LayoutDashboard, Globe } from "lucide-react";
import { useApp } from "../../context/AppContext";
import ConsentBanner from "./ConsentBanner";
import { fileUrl } from "../../lib/api";

function Logo({ testid }) {
  const { site } = useApp();
  return site.logo_url ? <img src={fileUrl(site.logo_url)} alt={site.store_name} className="h-8" data-testid={testid} />
    : <span data-testid={testid} className="font-display text-2xl tracking-tight">{site.store_name || "À l'abri"}</span>;
}

function LocalePicker() {
  const { config, locale, setLocale, t } = useApp();
  return (
    <div className="flex items-center gap-2 text-xs">
      <Globe className="w-3.5 h-3.5 text-muted-foreground" />
      <select data-testid="market-selector" aria-label={t("country")} value={locale.market} onChange={(e) => setLocale({ market: e.target.value })}
        className="bg-transparent border border-white/10 px-2 py-1 font-mono">
        {config.markets.map((m) => (
          <option key={m.code} value={m.code} disabled={!m.active} className="bg-[#14161D]" label={`${m.code} · ${m.currency}${!m.active ? ` (${t("coming_soon")})` : ""}`} />
        ))}
      </select>
      <select data-testid="language-selector" aria-label={t("language")} value={locale.lang} onChange={(e) => setLocale({ lang: e.target.value })}
        className="bg-transparent border border-white/10 px-2 py-1 font-mono">
        <option value="fr" className="bg-[#14161D]">FR</option>
        <option value="en" className="bg-[#14161D]">EN</option>
      </select>
    </div>
  );
}

function Header() {
  const { user, cart, t } = useApp();
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const count = cart.items?.reduce((a, i) => a + i.qty, 0) || 0;
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0B0C10]/75 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center gap-6">
        <Link to="/" aria-label="Accueil"><Logo testid="store-logo" /></Link>
        <nav className="hidden md:flex gap-5 text-sm text-muted-foreground">
          <NavLink to="/shop" data-testid="nav-shop" className="hover:text-white transition-colors">{t("shop")}</NavLink>
          <NavLink to="/help" data-testid="nav-help" className="hover:text-white transition-colors">{t("help")}</NavLink>
        </nav>
        <form className="flex-1 hidden sm:flex items-center border border-white/10 px-3 h-9 max-w-md" onSubmit={(e) => { e.preventDefault(); nav(`/shop?q=${encodeURIComponent(q)}`); }}>
          <Search className="w-4 h-4 text-muted-foreground" />
          <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="bg-transparent outline-none px-2 text-sm w-full" />
        </form>
        <div className="ml-auto flex items-center gap-4">
          <div className="hidden lg:block"><LocalePicker /></div>
          {user && ["owner", "admin"].includes(user.role) && (
            <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex items-center gap-1 text-xs font-mono text-[#10B981] border border-[#10B981]/30 px-2 py-1 hover:bg-[#10B981]/10 transition-colors">
              <LayoutDashboard className="w-3.5 h-3.5" />{t("admin")}
            </Link>
          )}
          <Link to={user ? "/account" : "/login"} data-testid="nav-account" aria-label={t("account")}><User className="w-5 h-5" /></Link>
          <Link to="/cart" data-testid="nav-cart" className="relative" aria-label={t("cart")}>
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[#C9A96E] text-black text-[10px] font-bold w-4 h-4 grid place-items-center rounded-full">{count}</span>}
          </Link>
        </div>
      </div>
      <div className="lg:hidden border-t border-white/5 px-5 py-2 flex justify-between items-center"><LocalePicker /><div className="flex gap-4"><Link to="/shop" className="text-xs" data-testid="mobile-shop-link">{t("shop")}</Link><Link to="/help" className="text-xs" data-testid="mobile-help-link">Aide</Link></div></div>
    </header>
  );
}

const LEGAL = [["cgv", "Conditions de vente"], ["confidentialite", "Confidentialité (Loi 25)"], ["remboursement", "Retours et remboursements"], ["expedition", "Livraison"], ["consentement", "Préférences de consentement"]];

function Footer() {
  const { config, t, site } = useApp();
  return (
    <footer className="border-t border-white/5 mt-24">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-10 text-sm">
        <div className="col-span-2">
          <Logo testid="footer-logo" />
          <p className="text-muted-foreground mt-3 max-w-sm">Prix affichés avant taxes (TPS et TVQ ajoutées au paiement), livraison incluse, délais estimés et conditions de retour visibles avant paiement.</p>
          <p className="text-xs text-muted-foreground mt-4" data-testid="footer-legal-info">{site.legal_name} · {site.legal_address}{site.neq && !site.neq.startsWith("[") ? ` · NEQ ${site.neq}` : ""}<br />{site.support_email}{site.support_phone ? ` · ${site.support_phone}` : ""}</p>
        </div>
        <div className="space-y-2">
          <div className="label-xs mb-3">{t("brands")}</div>
          {(site.categories || []).map((c) => <Link key={c} to={`/shop?category=${encodeURIComponent(c)}`} data-testid={`footer-cat-${c}`} className="block text-muted-foreground hover:text-white">{c}</Link>)}
          <Link to="/help" className="block text-muted-foreground hover:text-white" data-testid="footer-help">{t("help")} · FAQ · {t("contact")}</Link>
        </div>
        <div className="space-y-2">
          <div className="label-xs mb-3">{t("legal")}</div>
          {LEGAL.map(([s, l]) => <Link key={s} to={`/legal/${s}`} data-testid={`footer-legal-${s}`} className="block text-muted-foreground hover:text-white">{l}</Link>)}
        </div>
      </div>
    </footer>
  );
}

export default function StoreLayout() {
  const { site } = useApp();
  return (
    <div className="min-h-screen store-light">
      {site.banner_text && <div className="fixed top-0 inset-x-0 z-[60] h-7 grid place-items-center px-3 text-center whitespace-nowrap overflow-hidden text-ellipsis text-[10px] sm:text-[11px] tracking-[0.08em] sm:tracking-[0.2em] uppercase bg-[#1C1A17] text-[#F7F3EC]" data-testid="store-banner">{site.banner_text}</div>}
      <div className={site.banner_text ? "[&>header]:top-7" : ""}><Header /></div>
      <main className="pt-36 lg:pt-24 relative z-10"><Outlet /></main>
      <Footer />
      <ConsentBanner />
    </div>
  );
}
